import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Layouts } from 'app-components';
import { fetchHistoricalApplicationBasicInfo } from '@actions/historicalMemberApplication/historicalApplicationsActionCreator';
import { configs } from '@constants/configs/configs';
import { useAppDispatch, useAppSelector } from '@hooks/index';
import NavBarContainer from '@containers/common/NavBarContainer';
import { ConditionalRendering } from '@components/common/conditional-rendering';
import { HistoricalPageType } from './constants';
import HistoricalSideNav from './HistoricalSideNav';

// ============================================================================
// Styled Components
// ============================================================================

const LayoutContainer = styled.div`
    .grid-row {
        padding: 0;
    }

    .grid-col-12 {
        padding: 0;
    }

    #gi-sidebar {
        padding-left: 0;
        padding-right: 24px;
    }
`;

// ============================================================================
// Component
// ============================================================================

interface IHistoricalSideNavContainerProps {
    children: React.ReactNode;
    page: HistoricalPageType;
}

const HistoricalSideNavContainer: React.FC<IHistoricalSideNavContainerProps> = ({ children, page }) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const referenceId = searchParams.get('referenceId') || '';
    const { firstName, lastName } = useAppSelector(
        state => state.historicalApplications.historicalApplicationsBasicInfo
    );
    const [isInfoLoaded, setIsInfoLoaded] = useState(false);

    const navigateToNotFound = useCallback(() => {
        navigate(configs.routes.CAP_MEMBER_HISTORICAL_REFERENCE_ID_NOT_FOUND, { replace: true });
    }, [navigate]);

    useEffect(() => {
        if (!referenceId) {
            navigate(configs.routes.FALLBACK, { replace: true });
            return;
        }
        let cancelled = false;
        let navigatedAway = false;
        setIsInfoLoaded(false);
        const safeNavigateToNotFound = () => {
            if (!cancelled) {
                navigatedAway = true;
                navigateToNotFound();
            }
        };
        const loadBasicInfo = async () => {
            const success = await dispatch(
                fetchHistoricalApplicationBasicInfo(referenceId, safeNavigateToNotFound),
            );
            if (!cancelled && !navigatedAway && success) {
                setIsInfoLoaded(true);
            }
        };
        loadBasicInfo();
        return () => {
            cancelled = true;
        };
    }, [dispatch, referenceId]);

    return (
        <LayoutContainer>
            <Layouts id="sidenav-layout">
                <Layouts.Row hasGap>
                    <div className="grid-col-12">
                        <div className="grid-row">
                            <NavBarContainer
                                navBarClass="ticket-mgmt-top-nav grid-col-12"
                                houseIcon={false} />
                        </div>
                        <ConditionalRendering isTrue={isInfoLoaded}>
                            <div className="grid-row padding-t-20">
                                <h1 className="householdInfo" data-testid="householdInfo">
                                    {`${firstName ?? ''} ${lastName ?? ''}`}
                                </h1>
                            </div>
                        </ConditionalRendering>
                    </div>
                    <ConditionalRendering isTrue={isInfoLoaded}>
                        <HistoricalSideNav page={page} />
                        {children}
                    </ConditionalRendering>
                </Layouts.Row>
            </Layouts>
        </LayoutContainer>
    );
};

export default HistoricalSideNavContainer;
